import React, { useState } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Image,
  Type,
  AlignLeft,
  MousePointerClick,
  ChevronRight
} from "lucide-react";

function TemplateEditor({ template, onBack, onNext }) {
  const containerRef = React.useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(800);
  const [isResizing, setIsResizing] = useState(false);
  const [editData, setEditData] = useState(template || {
    id: null,
    name: "My Campaign",
    previewTitle: "",
    previewContent: "",
    previewButton: "Click Here",
    previewButtons: [],
    bgColor: "#f3f4f6",
    image: "🎁"
  });

  const [showButtonInputs, setShowButtonInputs] = useState(false);
  const [newButton, setNewButton] = useState({ name: "Click here", url: "" });

  const startResizing = React.useCallback(() => setIsResizing(true), []);
  const stopResizing = React.useCallback(() => setIsResizing(false), []);

  const resize = React.useCallback((e) => {
    if (isResizing && containerRef.current) {
      const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
      const maxWidth = window.innerWidth - 100;
      if (newWidth > 300 && newWidth < maxWidth) setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  React.useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, resize, stopResizing]);

  const handleChange = (field, value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  const handleAddButton = () => {
    if (newButton.name.trim()) {
      setEditData((prev) => ({
        ...prev,
        previewButtons: [...(prev.previewButtons || []), newButton],
      }));
      setNewButton({ name: "Click here", url: "" });
      setShowButtonInputs(false);
    }
  };

  const removeButton = (idx) =>
    setEditData((prev) => ({
      ...prev,
      previewButtons: prev.previewButtons.filter((_, i) => i !== idx),
    }));

  const COLOR_PRESETS = [
    "#f3f4f6", "#fecaca", "#bfdbfe", "#bbf7d0", "#fde68a",
    "#e9d5ff", "#fed7aa", "#fbcfe8", "#1f2937", "#7c3aed"
  ];

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100vh - 84px)", background: "#f8fafc" }}
      ref={containerRef}
    >
      {/* ── Header ── */}
      <header className="h-[68px] bg-white border-b border-gray-100 flex items-center justify-between px-6 flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="w-9 h-9 flex items-center justify-center border border-gray-100 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 transition-all"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Campaign Content</span>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Campaign name"
              className="text-sm font-black text-gray-900 uppercase tracking-tight border-none outline-none bg-transparent min-w-[200px] focus:ring-0 p-0"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-gray-500 border border-gray-100 rounded-xl hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => onNext(editData)}
            className="flex items-center gap-2 px-6 py-2 bg-gray-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-lg shadow-gray-900/20 active:scale-95"
          >
            Next Step <ChevronRight size={14} />
          </button>
        </div>
      </header>

      {/* ── Body: Sidebar + Resizer + Preview ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left Sidebar ── */}
        <aside
          className="bg-white border-r border-gray-100 overflow-y-auto flex-shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-8 space-y-8">
            {/* Section header */}
            <div>
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">Appearance</h2>
              <p className="text-[11px] font-medium text-gray-400 mt-1">Customize the look of your campaign card.</p>
            </div>

            {/* Cover Image */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Image size={12} /> Cover Image
              </label>
              <div
                className="h-36 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer relative overflow-hidden hover:border-gray-400 transition-all group"
                style={{ background: editData.bgColor }}
                onClick={() => document.getElementById("te-file-input").click()}
              >
                {editData.image && (editData.image.startsWith("data:") || editData.image.includes("/") || editData.image.includes(".webp") || editData.image.includes("static")) ? (
                  <img src={editData.image} alt="Preview" className="w-full h-full object-cover" />
                ) : null}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-2xl">
                  <span className="text-white text-[10px] font-bold uppercase tracking-widest">Change Image</span>
                </div>
              </div>
              <input id="te-file-input" type="file" className="hidden" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => handleChange("image", ev.target.result);
                  reader.readAsDataURL(file);
                }
              }} accept="image/*" />

              {/* Emoji shortcut */}
              <input
                type="text"
                value={editData.image && !editData.image.startsWith("data:") ? editData.image : ""}
                onChange={(e) => handleChange("image", e.target.value)}
                placeholder="Or type an emoji e.g. 🎁"
                className="mt-3 w-full px-4 py-2.5 text-sm border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all font-medium text-gray-900"
              />

              {/* Background color */}
              <div className="mt-4">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Background Color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      onClick={() => handleChange("bgColor", c)}
                      className="w-7 h-7 rounded-lg border-2 transition-all flex-shrink-0"
                      style={{
                        background: c,
                        borderColor: editData.bgColor === c ? "#111827" : "#e5e7eb",
                        transform: editData.bgColor === c ? "scale(1.15)" : "scale(1)",
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    value={editData.bgColor}
                    onChange={(e) => handleChange("bgColor", e.target.value)}
                    className="w-7 h-7 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
                    title="Custom color"
                  />
                </div>
              </div>
            </div>

            {/* Display Title */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Type size={12} /> Display Title
              </label>
              <input
                value={editData.previewTitle}
                onChange={(e) => handleChange("previewTitle", e.target.value)}
                placeholder="e.g. Special Offer!"
                className="w-full px-4 py-2.5 text-sm border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all font-medium text-gray-900"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <AlignLeft size={12} /> Description
              </label>
              <textarea
                value={editData.previewContent}
                onChange={(e) => handleChange("previewContent", e.target.value)}
                placeholder="Tell your visitors more..."
                rows={4}
                className="w-full px-4 py-2.5 text-sm border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all font-medium text-gray-900 resize-none"
              />
            </div>

            {/* Call to Action */}
            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <MousePointerClick size={12} /> Call to Action
                </label>
                <button
                  onClick={() => setShowButtonInputs(true)}
                  className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  <Plus size={11} /> Multi-Button
                </button>
              </div>

              {(editData.previewButtons || []).length > 0 ? (
                <div className="space-y-2">
                  {editData.previewButtons.map((btn, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-xl border border-gray-100">
                      <span className="flex-1 text-xs font-bold text-gray-700 truncate">{btn.name}</span>
                      <button
                        onClick={() => removeButton(idx)}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <input
                  value={editData.previewButton}
                  onChange={(e) => handleChange("previewButton", e.target.value)}
                  placeholder="Button text"
                  className="w-full px-4 py-2.5 text-sm border border-gray-100 rounded-xl bg-white focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-medium text-gray-900"
                />
              )}

              {showButtonInputs && (
                <div className="mt-4 p-4 bg-white rounded-xl border border-gray-100 space-y-3">
                  <input
                    className="w-full px-4 py-2 text-sm border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-medium"
                    placeholder="Button label"
                    value={newButton.name}
                    onChange={(e) => setNewButton({ ...newButton, name: e.target.value })}
                  />
                  <input
                    className="w-full px-4 py-2 text-sm border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-medium"
                    placeholder="URL (Optional)"
                    value={newButton.url}
                    onChange={(e) => setNewButton({ ...newButton, url: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddButton}
                      className="flex-1 py-2 bg-gray-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black transition-all active:scale-95"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setShowButtonInputs(false)}
                      className="px-4 py-2 border border-gray-100 text-gray-500 text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* ── Resizer ── */}
        <div
          className={`w-1 flex-shrink-0 cursor-col-resize relative group transition-colors ${isResizing ? "bg-indigo-500" : "bg-gray-100 hover:bg-indigo-400"}`}
          onMouseDown={startResizing}
        />

        {/* ── Preview Panel ── */}
        <main className="flex-1 flex flex-col items-center justify-center p-10 bg-[#f8fafc] overflow-auto">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">Live Preview</span>
          <div className="w-[340px] bg-white rounded-[28px] shadow-2xl shadow-gray-200/80 overflow-hidden border border-gray-100">
            {/* Image area */}
            <div
              className="h-48 flex items-center justify-center"
              style={{ background: editData.bgColor }}
            >
              {editData.image && (editData.image.startsWith("data:") || editData.image.includes("/") || editData.image.includes(".webp") || editData.image.includes("static")) ? (
                <img src={editData.image} alt="Cover" className="w-full h-full object-cover" />
              ) : null}
            </div>

            {/* Content */}
            <div className="p-8 text-center">
              <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight mb-3">
                {editData.previewTitle || "Your Title"}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-6">
                {editData.previewContent || "Your description goes here."}
              </p>
              {(editData.previewButtons || []).length > 0 ? (
                <div className="space-y-2">
                  {editData.previewButtons.map((btn, idx) => (
                    <button key={idx} className="w-full py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold text-gray-700 cursor-default">
                      {btn.name}
                    </button>
                  ))}
                </div>
              ) : (
                <button className="w-full py-3.5 bg-gray-900 text-white rounded-2xl text-sm font-bold uppercase tracking-widest cursor-default">
                  {editData.previewButton || "Learn More"}
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default TemplateEditor;
